import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, TrainFront, MapPinned, Camera, Timer, Briefcase, Car, Crown, Accessibility, Users,
  Search, GitCompare, CheckCircle2, ShieldCheck, Clock, CreditCard, Star, ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BookingWidget } from "@/components/BookingWidget";
import { Stars } from "@/components/Stars";
import { IMAGES } from "@/lib/constants";
import { api } from "@/lib/api";

const SERVICES = [
  { icon: Car, label: "Local Taxi", to: "/search", d: "Quick rides around town" },
  { icon: Plane, label: "Airport Transfer", to: "/airport-transfers", d: "All major UK airports" },
  { icon: TrainFront, label: "Station Transfer", to: "/station-transfers", d: "Rail & coach stations" },
  { icon: MapPinned, label: "Outstation", to: "/outstation", d: "Long-distance journeys" },
  { icon: Camera, label: "Tourist & Sightseeing", to: "/tourist", d: "Explore at your pace" },
  { icon: Timer, label: "Hourly Hire", to: "/search", d: "Driver by the hour" },
  { icon: Briefcase, label: "Corporate Travel", to: "/corporate", d: "Business accounts" },
  { icon: Accessibility, label: "Wheelchair Accessible", to: "/search", d: "WAV vehicles" },
];
const DESTS = ["London", "Manchester", "Birmingham", "Edinburgh", "Oxford", "Leeds", "Liverpool", "Bristol"];
const VEHICLES = [
  { key: "saloon", label: "Saloon", pax: 4, bags: 2, img: "https://images.unsplash.com/photo-1550431528-122aefbbd2a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", icon: Car },
  { key: "estate", label: "Estate", pax: 4, bags: 3, img: IMAGES.cabRain, icon: Car },
  { key: "executive", label: "Executive", pax: 4, bags: 2, img: IMAGES.mercedes, icon: Crown },
  { key: "mpv", label: "MPV / 6-8 Seater", pax: 8, bags: 6, img: "https://images.unsplash.com/photo-1589148938909-4d241c91ee52?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", icon: Users },
];
const TRUST = [
  "Verified taxi partners", "Transparent booking information", "Airport & station specialists",
  "Local and long-distance journeys", "Customer support", "Secure online booking",
  "Multiple vehicle options", "Easy booking management",
];

function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <div className={`mb-10 ${center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}`}>
      {eyebrow && <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>}
      <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">{title}</h2>
      {sub && <p className="mt-3 text-base leading-relaxed text-slate-600">{sub}</p>}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    api.get("/reviews", { params: { limit: 6 } }).then(({ data }) => setReviews(data)).catch(() => {});
    api.get("/faqs").then(({ data }) => setFaqs(data)).catch(() => {});
    api.get("/cms/home").then(({ data }) => setCms(data && data.id ? data : null)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy">
        <img src={IMAGES.cabStreet} alt="London taxi" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/70 to-navy" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="fade-up lg:col-span-5">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                <ShieldCheck size={14} className="text-emerald-400" /> Your Ride. Your Fare. Your Journey.
              </span>
              <h1 className="mt-5 font-heading text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                {cms?.hero_title
                  ? cms.hero_title
                  : <>Book Your Taxi<br />Across the UK</>}
              </h1>
              <p className="mt-5 max-w-md text-lg text-slate-300">
                {cms?.hero_subtitle || "Reliable local taxis, airport transfers, station pickups and long-distance journeys — all in one place."}
              </p>
              <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-300">
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400" /> Fixed, upfront fares</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400" /> 20+ trusted operators</span>
              </div>
            </div>
            <div className="fade-up lg:col-span-7">
              <BookingWidget />
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR BOOKING TYPES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <SectionHead eyebrow="Popular booking types" title="Every journey, one platform" sub="From a quick local ride to a cross-country transfer — choose the service that fits your journey." />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {SERVICES.map((s) => (
            <button key={s.label} onClick={() => navigate(s.to)} data-testid={`service-${s.label.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:-translate-y-1 hover:border-brand hover:shadow-lg">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light text-brand transition group-hover:bg-brand group-hover:text-white"><s.icon size={20} /></span>
              <h3 className="mt-3 font-heading text-sm font-semibold text-navy">{s.label}</h3>
              <p className="mt-1 text-xs text-slate-500">{s.d}</p>
            </button>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead center eyebrow="How it works" title="Book in three simple steps" />
          <div className="grid gap-6 md:grid-cols-3">
            {[[Search, "1. Search", "Enter pickup, destination, date and time."],
              [GitCompare, "2. Compare", "See available taxis, vehicles and fares."],
              [CheckCircle2, "3. Book", "Select your taxi and confirm securely."]].map(([Icon, t, d], i) => (
              <div key={t} className="relative rounded-2xl border border-slate-200 bg-white p-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-white"><Icon size={24} /></span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{t}</h3>
                <p className="mt-2 text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.airport} alt="Airport" className="h-80 w-full object-cover" />
          </div>
          <div>
            <SectionHead eyebrow="Airport transfers" title="Never miss a flight" sub="Meet & greet, flight monitoring and fixed fares to and from every major UK airport." />
            <div className="grid grid-cols-2 gap-3">
              {["Heathrow", "Gatwick", "Manchester", "Stansted", "Luton", "Birmingham"].map((a) => (
                <button key={a} onClick={() => navigate("/airport-transfers")} data-testid={`airport-${a.toLowerCase()}`}
                  className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-navy transition hover:border-brand hover:bg-brand-light">
                  {a} <ArrowRight size={15} className="text-brand" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR DESTINATIONS */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead center eyebrow="Popular UK destinations" title="Trusted taxis in every city" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {DESTS.map((d) => (
              <button key={d} onClick={() => navigate("/search")} data-testid={`dest-${d.toLowerCase()}`}
                className="group relative h-32 overflow-hidden rounded-2xl border border-slate-200">
                <img src={IMAGES.skyline} alt={d} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-navy/50" />
                <span className="absolute inset-x-0 bottom-0 p-3 text-left font-heading font-semibold text-white">Taxis in {d}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* OUTSTATION + TOURIST */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid gap-6 lg:grid-cols-2">
          <div onClick={() => navigate("/outstation")} data-testid="home-outstation" className="group relative cursor-pointer overflow-hidden rounded-3xl">
            <img src={IMAGES.countryside} alt="Outstation" className="h-72 w-full object-cover transition duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <h3 className="font-heading text-2xl font-bold text-white">Outstation Journeys</h3>
              <p className="mt-1 text-sm text-slate-200">Travel across the UK without the stress. London → Manchester, Birmingham, Oxford and more.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-white">Explore routes <ArrowRight size={15} /></span>
            </div>
          </div>
          <div onClick={() => navigate("/tourist")} data-testid="home-tourist" className="group relative cursor-pointer overflow-hidden rounded-3xl">
            <img src={IMAGES.skyline} alt="Tourist" className="h-72 w-full object-cover transition duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <h3 className="font-heading text-2xl font-bold text-white">Tourist Travel</h3>
              <p className="mt-1 text-sm text-slate-200">Explore the UK your way with a private driver, flexible stops and custom itineraries.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-white">Plan a trip <ArrowRight size={15} /></span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-navy py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">Why SmartFares4U</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-white sm:text-3xl lg:text-4xl">Travel with confidence</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map((t) => (
              <div key={t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-400" />
                <span className="text-sm font-medium text-slate-200">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VEHICLES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHead center eyebrow="Our fleet" title="Available vehicle types" sub="From everyday saloons to executive Mercedes and 8-seater people carriers." />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VEHICLES.map((v) => (
            <div key={v.key} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:shadow-lg">
              <div className="h-40 overflow-hidden bg-slate-100"><img src={v.img} alt={v.label} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /></div>
              <div className="p-5">
                <div className="flex items-center gap-2"><v.icon size={16} className="text-brand" /><h3 className="font-heading font-semibold text-navy">{v.label}</h3></div>
                <p className="mt-2 text-sm text-slate-500">Up to {v.pax} passengers · {v.bags} bags</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead center eyebrow="Customer reviews" title="Loved by UK travellers" />
          <div className="grid gap-6 md:grid-cols-3">
            {reviews.slice(0, 6).map((r) => (
              <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-6">
                <Quote size={24} className="text-brand/30" />
                <p className="mt-3 text-sm leading-relaxed text-slate-700">"{r.text}"</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm font-semibold text-navy">{r.customer_name}</span>
                  <Stars rating={r.rating} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BECOME A DRIVER */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid items-center gap-10 overflow-hidden rounded-3xl border border-slate-200 bg-white lg:grid-cols-2">
          <div className="p-8 lg:p-12">
            <SectionHead eyebrow="Partner with us" title="Grow your taxi business with SmartFares4U" sub="More bookings, digital booking management and a central dashboard for local, airport and long-distance jobs." />
            <Button size="lg" className="bg-brand hover:bg-brand/90" onClick={() => navigate("/become-a-driver")} data-testid="become-driver-cta">Apply Now</Button>
          </div>
          <img src={IMAGES.executiveDriver} alt="Driver" className="h-72 w-full object-cover lg:h-full" />
        </div>
      </section>

      {/* CORPORATE */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <img src={IMAGES.mercedes} alt="Corporate" className="h-72 w-full rounded-3xl object-cover" />
            <div>
              <SectionHead eyebrow="Corporate travel" title="Reliable travel for your business" sub="Corporate accounts, monthly billing, dedicated support and complete booking history." />
              <Button size="lg" variant="outline" onClick={() => navigate("/corporate")} data-testid="corporate-cta">Create Corporate Account</Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHead center eyebrow="FAQ" title="Frequently asked questions" />
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f) => (
            <AccordionItem key={f.id} value={f.id} data-testid={`faq-${f.id}`}>
              <AccordionTrigger className="text-left font-heading text-base font-semibold text-navy">{f.question}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-navy p-10 text-center lg:p-16">
          <img src={IMAGES.cabRain} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
          <div className="relative">
            <h2 className="font-heading text-3xl font-bold text-white lg:text-4xl">Ready to book your journey?</h2>
            <p className="mx-auto mt-3 max-w-lg text-slate-300">Compare trusted taxis across the UK and book in under a minute.</p>
            <Button size="lg" className="mt-6 bg-brand hover:bg-brand/90" onClick={() => navigate("/search")} data-testid="final-cta-btn">Search Available Taxis</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
